import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { INDUSTRIES } from "@/data/content";

export default function Industries() {
  return (
    <div data-testid="industries-page">
      <SEO
        title="Industries We Serve — Banking, Energy, Government, Retail | Notion Minds Systems"
        description="Industry-aware technology consulting for banking & capital markets, energy & utilities, government & giga-projects, and retail & logistics across Saudi Arabia, Oman and Qatar."
        canonical="https://notionminds.sa/industries"
      />
      <section className="pt-24 pb-12 max-w-[1440px] mx-auto px-6 lg:px-12">
        <p className="font-mono-accent text-xs text-[#d4af37] mb-5">[ Industries ]</p>
        <h1 className="font-display text-5xl lg:text-7xl font-light leading-[1.05] max-w-4xl">
          Deep verticals.<br />
          <span className="text-gradient-gold font-semibold">Built on Saudi, Omani & Qatari ground truth.</span>
        </h1>
        <p className="mt-8 text-white/65 text-lg max-w-2xl">
          Our partners and engineers come from the industries we serve — so context
          is the default, not the consulting upsell.
        </p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1a1a20] border border-[#1a1a20]">
          {INDUSTRIES.map((ind, i) => (
            <motion.div
              key={ind.id}
              data-testid={`industry-${ind.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="group bg-[#050505] hover:bg-[#0d0d14] p-12 transition-colors relative overflow-hidden"
            >
              <div className="flex items-center justify-between mb-8">
                <span className="font-mono-accent text-xs text-white/40">
                  Industry / 0{i + 1}
                </span>
                <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-[#d4af37] group-hover:rotate-12 transition-all" />
              </div>
              <h2 className="font-display text-3xl lg:text-4xl font-light mb-5 group-hover:text-[#d4af37] transition-colors">
                {ind.name}
              </h2>
              <p className="text-white/60 leading-relaxed max-w-md">{ind.blurb}</p>
              <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap gap-2">
                {["AI", "Cyber", "Cloud", "OT"].map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] font-mono-accent text-white/40 border border-white/10 px-2 py-1"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 p-12 border border-[#d4af37]/30 grid-pattern-fine">
          <div className="max-w-2xl">
            <p className="font-mono-accent text-xs text-[#d4af37] mb-4">Sector-specific accelerators</p>
            <h3 className="font-display text-3xl lg:text-4xl font-light mb-6">
              Tell us your sector — we'll share the relevant playbook.
            </h3>
            <Link
              to="/contact"
              data-testid="industries-contact-cta"
              className="inline-flex items-center gap-2 bg-[#d4af37] text-black px-7 py-3.5 font-semibold hover:bg-[#e5c059] transition-colors"
            >
              Request sector playbook <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
